import React, { useMemo } from "react";
import CurrencyFormat from "react-currency-format";
import TripExtrasStoreSheet from "./TripExtrasStoreSheet";
import {
  estimateExtrasFromCart,
  extrasCartSignature,
  extrasLinesSignature,
} from "./tripExtrasHelpers";

const TripExtrasPanel = ({
  catalog,
  cart = {},
  onChangeQty,
  onOpenSheet,
  sheetOpen,
  onCloseSheet,
  loading = false,
  allInPriceCents = 0,
  extrasSubtotalCents = 0,
  extrasLines = [],
}) => {
  const featured = useMemo(() => {
    const list = catalog?.featured || [];
    if (list.length) return list[0];
    const flat = (catalog?.categories || []).flatMap((c) => c.products || []);
    return flat[0] || null;
  }, [catalog]);

  const { totalCents: previewExtrasCents } = useMemo(
    () => estimateExtrasFromCart(catalog, cart),
    [catalog, cart],
  );

  const cartItemCount = Object.values(cart).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0,
  );

  const cartMatchesQuote =
    extrasCartSignature(cart) === extrasLinesSignature(extrasLines);
  const tripPriceCents = Math.max(0, allInPriceCents - extrasSubtotalCents);
  const displayExtrasCents =
    cartMatchesQuote && !loading ? extrasSubtotalCents : previewExtrasCents;
  const showPreviewHint = !cartMatchesQuote && !loading && cartItemCount > 0;

  const featuredQty = featured ? cart[featured.productId] || 0 : 0;
  const featuredUnit =
    featured?.salePrice != null && featured.salePrice > 0
      ? featured.salePrice
      : featured?.price;

  return (
    <div
      className={"lp2-extras" + (loading ? " is-refreshing" : "")}
      id="lp2-trip-extras"
    >
      <div className="lp2-extras-head">
        <span className="lp2-extras-head-title">Moving supplies</span>
        <span className="lp2-extras-head-sub">Included in your all-in price</span>
      </div>

      {featured ? (
        <div className="lp2-extras-row">
          {featured.image ? (
            <img src={featured.image} alt="" className="lp2-extras-row-thumb" />
          ) : (
            <span className="lp2-extras-row-thumb lp2-extras-row-thumb--ph">
              <i className="mdi mdi-package-variant" />
            </span>
          )}
          <div className="lp2-extras-row-body">
            <span className="lp2-extras-row-name">{featured.name}</span>
            <span className="lp2-extras-row-price">
              <CurrencyFormat
                value={featuredUnit}
                displayType="text"
                thousandSeparator
                prefix="R"
                decimalScale={0}
              />
            </span>
          </div>
          <div className="lp2-extras-row-action">
            {featuredQty > 0 ? (
              <div className="lp2-extras-stepper lp2-extras-stepper--row">
                <button
                  type="button"
                  className="lp2-extras-stepper-btn"
                  aria-label={`Decrease ${featured.name}`}
                  onClick={() => onChangeQty(featured.productId, featuredQty - 1)}
                >
                  −
                </button>
                <span className="lp2-extras-stepper-qty">{featuredQty}</span>
                <button
                  type="button"
                  className="lp2-extras-stepper-btn"
                  aria-label={`Increase ${featured.name}`}
                  onClick={() => onChangeQty(featured.productId, featuredQty + 1)}
                >
                  +
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="lp2-extras-add-btn"
                aria-label={`Add ${featured.name}`}
                onClick={() => onChangeQty(featured.productId, 1)}
              >
                +
              </button>
            )}
          </div>
        </div>
      ) : null}

      <div className="lp2-extras-foot">
        {cartItemCount > 0 ? (
          <button type="button" className="lp2-extras-selection" onClick={onOpenSheet}>
            {cartItemCount} item{cartItemCount === 1 ? "" : "s"}
            {displayExtrasCents > 0 ? (
              <>
                {" · +"}
                <CurrencyFormat
                  value={displayExtrasCents / 100}
                  displayType="text"
                  thousandSeparator
                  prefix="R"
                  decimalScale={2}
                />
                {showPreviewHint ? " (preview)" : loading ? " (updating…)" : ""}
              </>
            ) : null}
          </button>
        ) : (
          <span className="lp2-extras-foot-spacer" />
        )}
        <button
          type="button"
          className="lp2-extras-shop-link"
          onClick={onOpenSheet}
          aria-expanded={sheetOpen}
        >
          Shop supplies
          {catalog?.totalCount ? ` (${catalog.totalCount})` : ""}
          <i className="mdi mdi-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <TripExtrasStoreSheet
        open={sheetOpen}
        onClose={onCloseSheet}
        catalog={catalog}
        categories={catalog?.categories || []}
        totalCount={catalog?.totalCount || 0}
        cart={cart}
        onChangeQty={onChangeQty}
        quoteLoading={loading}
        tripPriceCents={tripPriceCents}
        confirmedExtrasCents={extrasSubtotalCents}
        confirmedAllInCents={allInPriceCents}
        confirmedExtrasLines={extrasLines}
      />
    </div>
  );
};

export default TripExtrasPanel;
